import React, { useEffect, useState } from 'react';
import { listarProcessos, atualizarProcesso as atualizarProcessoLocal } from '../servicos/servicoGerenciadorProcessos';
import { listarProcessos as listarProcessosApi, atualizarProcesso as atualizarProcessoApi } from '../servicos/servicoApiProcessos';
import type { Processo } from '../servicos/servicoGerenciadorProcessos';
import type { ProcessoApi } from '../servicos/servicoApiProcessos';
import { CardProcesso } from '../componentes/CardProcesso';
import { ListaProcessos } from '../componentes/ListaProcessos';
import ViewToggle from '../componentes/ViewToggle';
import { excluirProcesso } from '../servicos/servicoApiProcessos';
import { FilePlus, Users, User, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TipoVerticalProcesso, LABELS_TIPO_VERTICAL } from '../tipos/tiposVerticais';
import { useAutenticacao } from '../../../core/contextos/ContextoAutenticacao';
import { useOrganizacao } from '../../../core/contextos/ContextoOrganizacao';

interface ApiProcesso extends ProcessoApi {
  arquivado?: boolean;
  status?: string;
  id?: string;
  _id?: string | number;
  tipo?: string;
}

// Tipo para o filtro de propriedade (meus vs organização)
type FiltroPropriedade = 'todos' | 'meus';

/**
 * DashboardProcessos
 *
 * CONTEXTO DE NEGÓCIO:
 * Página que exibe todos os processos gerenciados localmente (representação de processos
 * do usuário). Permite filtrar e executar ações rápidas em cada processo (Continuar, Ver Detalhes).
 *
 * MULTI-TENANT:
 * - Se o usuário faz parte de uma organização, mostra processos compartilhados
 * - Filtro para alternar entre "Meus processos" e "Todos da organização"
 * - Exibe o nome do criador quando o processo não é do próprio usuário
 *
 * IMPLEMENTAÇÃO:
 * - Usa o serviço `servicoGerenciadorProcessos` como fonte de verdade
 * - Lista os processos em formato de grid com cards reutilizáveis (CardProcesso)
 * - Estado local para filtros e lista de processos
 */
export function DashboardProcessos(): JSX.Element {
  const navigate = useNavigate();
  const { usuario } = useAutenticacao();
  const { organizacaoAtual } = useOrganizacao();
  
  const [processos, setProcessos] = useState<Processo[]>([]);
  const [carregando, setCarregando] = useState<boolean>(true);
  const [filtro, setFiltro] = useState<'todos' | 'andamento' | 'concluidos'>('todos');
  const [filtroVertical, setFiltroVertical] = useState<string>('todos'); // TAREFA-224: Filtro por tipo de vertical
  const [filtroPropriedade, setFiltroPropriedade] = useState<FiltroPropriedade>('todos'); // MULTI-TENANT: Filtro meus vs organização
  const [visualizacao, setVisualizacao] = useState<'cards' | 'lista'>(() => {
    // Persistência da preferência do usuário
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('dashboard_visualizacao') as 'cards' | 'lista') || 'cards';
    }
    return 'cards';
  });
  useEffect(() => {
    // Persistir preferência ao trocar
    if (typeof window !== 'undefined') {
      localStorage.setItem('dashboard_visualizacao', visualizacao);
    }
  }, [visualizacao]);

  useEffect(() => {
    carregarProcessos();
  }, []);

  /**
   * NOTA: WebSocket REMOVIDO do Dashboard (TAREFA-094 revisada)
   * 
   * CONTEXTO:
   * O endpoint WebSocket `/ws/analise/status/{upload_id}` requer um `upload_id`
   * específico para estabelecer a conexão. No Dashboard, listamos múltiplos
   * processos e não temos um único `upload_id` para monitorar.
   * 
   * DECISÃO:
   * WebSocket é usado apenas em páginas de análise individual (AnalisePeticaoInicial,
   * CriacaoPeticaoInicial) onde há um `upload_id` específico sendo processado.
   * O Dashboard usa carregamento por demanda (pull-to-refresh ou auto-refresh).
   * 
   * FUTURO:
   * Se necessário atualizações em tempo real no Dashboard, criar um endpoint
   * WebSocket genérico que transmita broadcast de todos os processos do usuário.
   */

  function carregarProcessos() {
    (async () => {
      setCarregando(true);
      try {
        // Tentar carregar do backend primeiro
        const resp = await listarProcessosApi();
        // API pode retornar array direto ou { processos: [...] }
        const resultado = Array.isArray(resp.data) ? resp.data : resp.data?.processos || [];
        // Filtrar processos arquivados (soft-deleted)
        const ativoResultado = resultado.filter((p: ApiProcesso) => !p?.arquivado);
        if (resultado.length > 0) {
          // Mapear para o formato local (Processo)
          const mapEstadoParaEtapa = (estado: string): number => {
            const normalizado = (estado || '').toLowerCase();
            if (normalizado.includes('upload')) return 1;
            if (normalizado.includes('complementar') || normalizado.includes('documentos')) return 2;
            if (normalizado.includes('sindical') || normalizado.includes('jurispr')) return 3;
            if (normalizado.includes('selecion') || normalizado.includes('agente')) return 4;
            if (normalizado.includes('process')) return 5;
            if (normalizado.includes('done') || normalizado.includes('concl')) return 6;
            return 1;
          };

          const TOTAL_ETAPAS = 6;

          const mapeados = ativoResultado.map((p: ApiProcesso) => {
            const estado = p.estado_wizard || (p.metadados && typeof p.metadados.estado_wizard === 'string' ? p.metadados.estado_wizard : '') || '';
            const isProcessing = (p.status_processamento === 'processing' || p.status === 'processing');

            // TAREFA-224: Usar progresso e etapa_atual da API se disponíveis, senão calcular
            let progressoFinal: number;
            let etapaAtualFinal: number;

            if (typeof p.progresso === 'number' && typeof p.etapa_atual === 'number') {
              // API já fornece progresso e etapa_atual (do orquestrador)
              progressoFinal = p.progresso;
              etapaAtualFinal = p.etapa_atual;
            } else {
              // Fallback: calcular baseado no estado_wizard (processos antigos)
              const etapaNumero = mapEstadoParaEtapa(estado); // 1..6
              etapaAtualFinal = Math.max(0, etapaNumero - 1);
              progressoFinal = Math.round((etapaNumero / TOTAL_ETAPAS) * 100);
            }

            // Consideramos "aguardando usuário" quando o backend NÃO está processando
            // e o progresso não está em 100%
            const aguardandoUsuario = !isProcessing && progressoFinal < 100;

            // TAREFA-224: Mapear tipo_vertical da API, garantindo que processos sejam corretamente categorizados
            let tipoVertical = p.tipo_vertical || 'analise_processual';

            // Se tipo_vertical não está definido mas podemos inferir do tipo ou nome do processo
            if (!p.tipo_vertical) {
              const nomeProcesso = (p.nome || '').toLowerCase();
              const tipoProcesso = (p.tipo || '').toLowerCase();

              if (nomeProcesso.includes('criação') || nomeProcesso.includes('criacao') ||
                nomeProcesso.includes('petição') || nomeProcesso.includes('peticao') ||
                tipoProcesso.includes('criacao') || tipoProcesso === 'criacao_peticao') {
                tipoVertical = 'criacao_peticao';
              }
            }

            // MULTI-TENANT: Usar eh_meu da API ou calcular como fallback
            const usuarioIdProcesso = p.usuario_id;
            const ehMeu = p.eh_meu !== undefined ? p.eh_meu : (usuario ? usuarioIdProcesso === usuario.id : true);

            return ({
              id: p.processo_id || p.id || String(p._id || ''),
              tipo: p.tipo || 'analise',
              nome: p.nome || p.descricao || 'Processo',
              status: isProcessing ? 'em_andamento' : (p.status_processamento === 'done' || p.status === 'concluido' || progressoFinal === 100) ? 'concluido' : 'pendente',
              etapaAtual: etapaAtualFinal,
              progresso: progressoFinal,
              dados: { ...(p.metadados || {}), estado_wizard: estado, aguardando_usuario: aguardandoUsuario },
              historico: [] as string[],
              tipoVertical, // TAREFA-224: Tipo de vertical mapeado corretamente
              // MULTI-TENANT: Informações do dono/organização
              usuarioId: usuarioIdProcesso,
              nomeUsuario: p.nome_usuario,
              organizacaoId: p.organizacao_id,
              ehMeu,
            } as Processo);
          });
          setProcessos(mapeados);
          setCarregando(false);
          return;
        }
      } catch (err) {
        console.warn('[DashboardProcessos] Falha ao obter processos do backend, usando fallback local:', err);
      }

      // Fallback: usar armazenamento local
      const todos = listarProcessos();
      setProcessos(todos);
      setCarregando(false);
    })();
  }

  // Nota: função de arquivar foi removida do UI; mantemos a função de exclusão/renomeação

  async function handleExcluir(processoId: string) {
    try {
      await excluirProcesso(processoId);
      carregarProcessos();
    } catch (err) {
      console.error('[DashboardProcessos] Erro ao excluir processo:', err);
      const restantes = processos.filter(p => p.id !== processoId);
      setProcessos(restantes);
    }
  }

  async function handleRenomear(processoId: string, novoNome: string) {
    try {
      await atualizarProcessoApi(processoId, { nome: novoNome });
      // Recarregar lista para garantir consistência com backend
      carregarProcessos();
    } catch (err) {
      console.warn('[DashboardProcessos] Falha ao renomear via API, aplicando fallback local:', err);
      try {
        const atualizado = atualizarProcessoLocal(processoId, { nome: novoNome });
        if (atualizado) {
          // Atualiza o estado local sem recarregar do backend
          setProcessos(prev => prev.map(p => p.id === processoId ? { ...p, nome: atualizado.nome } : p));
        }
      } catch (err2) {
        console.error('[DashboardProcessos] Falha ao aplicar fallback local para renomear:', err2);
      }
    }
  }

  function aplicarFiltro(lista: Processo[]): Processo[] {
    let resultado = lista;

    // Filtro por status
    if (filtro !== 'todos') {
      if (filtro === 'andamento') {
        resultado = resultado.filter(p => p.status === 'em_andamento' || p.status === 'pendente');
      } else {
        resultado = resultado.filter(p => p.status === 'concluido');
      }
    }

    // TAREFA-224: Filtro por tipo de vertical
    if (filtroVertical !== 'todos') {
      resultado = resultado.filter(p => p.tipoVertical === filtroVertical);
    }

    // MULTI-TENANT: Filtro por propriedade (meus vs todos da organização)
    if (filtroPropriedade === 'meus') {
      resultado = resultado.filter(p => p.ehMeu === true);
    }

    return resultado;
  }

  const listaFiltrada = aplicarFiltro(processos);
  
  // MULTI-TENANT: Contar processos próprios vs da organização para exibição
  const totalMeus = processos.filter(p => p.ehMeu === true).length;
  const totalOrganizacao = processos.length;

  return (
    <div className="overflow-x-hidden p-5">
      <h1 className="mb-2 text-2xl font-bold">Dashboard de Processos</h1>
      <p className="mb-4 text-gray-700">
        {organizacaoAtual 
          ? `Processos do escritório ${organizacaoAtual.nome}. Você pode ver análises suas e de colegas.`
          : 'Visão geral dos seus processos. Aqui você encontra todos os processos ativos, em andamento e concluídos.'}
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-4">
        {/* MULTI-TENANT: Filtro de propriedade (se tem organização) */}
        {organizacaoAtual && (
          <div className="flex rounded-lg border border-gray-300 overflow-hidden" role="group" aria-label="Filtro de propriedade">
            <button
              onClick={() => setFiltroPropriedade('todos')}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium transition-colors ${
                filtroPropriedade === 'todos' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Users className="size-4" />
              Todos ({totalOrganizacao})
            </button>
            <button
              onClick={() => setFiltroPropriedade('meus')}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-300 ${
                filtroPropriedade === 'meus' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <User className="size-4" />
              Meus ({totalMeus})
            </button>
          </div>
        )}
        
        <div className="flex gap-2" role="group" aria-label="Filtro de status">
          <label className="flex items-center gap-1">
            <input
              type="radio"
              checked={filtro === 'todos'}
              onChange={() => setFiltro('todos')}
            />{' '}
            Todos
          </label>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              checked={filtro === 'andamento'}
              onChange={() => setFiltro('andamento')}
            />{' '}
            Em andamento
          </label>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              checked={filtro === 'concluidos'}
              onChange={() => setFiltro('concluidos')}
            />{' '}
            Concluídos
          </label>
        </div>
        {/* TAREFA-224: Filtro por tipo de vertical */}
        <div className="flex items-center gap-2">
          <label htmlFor="filtro-vertical" className="text-sm font-medium text-gray-700">
            Tipo:
          </label>
          <select
            id="filtro-vertical"
            value={filtroVertical}
            onChange={(e) => setFiltroVertical(e.target.value)}
            className="rounded border border-gray-300 px-3 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="todos">Todos os Tipos</option>
            <option value={TipoVerticalProcesso.ANALISE_PROCESSUAL}>
              {LABELS_TIPO_VERTICAL[TipoVerticalProcesso.ANALISE_PROCESSUAL]}
            </option>
            <option value={TipoVerticalProcesso.CRIACAO_PETICAO}>
              {LABELS_TIPO_VERTICAL[TipoVerticalProcesso.CRIACAO_PETICAO]}
            </option>
            <option value={TipoVerticalProcesso.CONTESTACAO}>
              {LABELS_TIPO_VERTICAL[TipoVerticalProcesso.CONTESTACAO]}
            </option>
            <option value={TipoVerticalProcesso.RECURSO}>
              {LABELS_TIPO_VERTICAL[TipoVerticalProcesso.RECURSO]}
            </option>
          </select>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <ViewToggle mode={visualizacao} onChange={(m) => setVisualizacao(m)} />
          <button
            onClick={() => navigate('/criacao-peticao-inicial')}
            className="ml-2 inline-flex items-center gap-2 rounded-lg bg-primary-600 px-3 py-2 text-white hover:bg-primary-700"
          >
            <FilePlus className="size-4" />
            Iniciar Nova Petição
          </button>
        </div>
      </div>

      {carregando ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          <Loader2 className="mb-4 h-10 w-10 animate-spin text-primary-600" />
          <p className="text-lg">Carregando processos...</p>
        </div>
      ) : listaFiltrada.length === 0 ? (
        <div className="rounded border border-dashed border-gray-300 p-8 text-center text-gray-500">
          Nenhum processo encontrado para o filtro selecionado.
        </div>
      ) : visualizacao === 'cards' ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {listaFiltrada.map((proc) => (
            <CardProcesso key={proc.id} processo={proc} onExcluir={handleExcluir} onRenomear={handleRenomear} />
          ))}
        </div>
      ) : (
        <ListaProcessos processos={listaFiltrada} onExcluir={handleExcluir} onRenomear={handleRenomear} />
      )}
    </div>
  );
}
